/**
 * Audit Page - SuperAdmin only
 *
 * Allows searching for requests by requestNumber and displaying their audit trail
 */

import React, { useState } from 'react';
import { Input, Button, Card, Tag, Typography, Space, Divider, Select, Empty, Spin, Alert, theme } from 'antd';
import { SearchOutlined, ClockCircleOutlined, UserOutlined, DollarOutlined } from '@ant-design/icons';
import { auditService } from '../../services/auditService';
import { AuditData, AuditLog } from '../../types/audit';
import { useTheme } from '../../contexts/ThemeContext';
import './Audit.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { useToken } = theme;

export const Audit: React.FC = () => {
  const { token } = useToken();
  const { theme: currentTheme } = useTheme();
  const [requestNumber, setRequestNumber] = useState<string>('');
  const [auditData, setAuditData] = useState<AuditData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [logLevelFilter, setLogLevelFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');

  const handleSearch = async () => {
    if (!requestNumber.trim()) {
      setError('Por favor ingrese un número de solicitud');
      return;
    }

    setLoading(true);
    setError(null);
    setAuditData(null);

    try {
      const response = await auditService.getAuditByRequestNumber(requestNumber.trim());

      if (response.success && response.data) {
        setAuditData(response.data);
      } else {
        setError(response.message || 'No se encontró la solicitud');
      }
    } catch (err: any) {
      setError(err.message || 'Error al buscar la auditoría');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): string => {
    const statusColors: Record<string, string> = {
      'PENDING': 'orange',
      'APPROVED': 'blue',
      'COMPLETED': 'green',
      'REJECTED': 'red',
      'PROCESSING': 'cyan',
    };
    return statusColors[status] || 'default';
  };

  const getTypeColor = (type: string): string => {
    return type === 'LOAD' ? 'blue' : 'purple';
  };

  const getLogLevelColor = (level: string): string => {
    const levelColors: Record<string, string> = {
      'debug': 'default',
      'info': 'blue',
      'warn': 'orange',
      'error': 'red',
    };
    return levelColors[level] || 'default';
  };

  const filteredLogs = auditData?.auditTrail.logs.filter((log: AuditLog) => {
    // Filter by log level
    if (logLevelFilter !== 'all' && log.level !== logLevelFilter) {
      return false;
    }

    // Filter by search text (search in phase, message, and data)
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      const messageMatch = log.message.toLowerCase().includes(searchLower);
      const phaseMatch = log.phase.toLowerCase().includes(searchLower);
      const dataMatch = log.data ? JSON.stringify(log.data).toLowerCase().includes(searchLower) : false;

      return messageMatch || phaseMatch || dataMatch;
    }

    return true;
  }) || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="audit-container">
      <Card>
        <Title level={2}>Auditoría de Solicitudes</Title>
        <Text type="secondary">Busca una solicitud por su número para ver el registro de auditoría completo</Text>

        <div className="search-section">
          <Space.Compact style={{ width: '100%', maxWidth: 600, marginTop: 24 }}>
            <Input
              placeholder="Ingrese el número de solicitud (ej: LR-00001 o WR-00001)"
              value={requestNumber}
              onChange={(e) => setRequestNumber(e.target.value)}
              onPressEnter={handleSearch}
              size="large"
              disabled={loading}
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
              size="large"
            >
              Buscar
            </Button>
          </Space.Compact>
        </div>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            closable
            onClose={() => setError(null)}
            style={{ marginTop: 24 }}
          />
        )}

        {loading && (
          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <Spin size="large" />
          </div>
        )}

        {!loading && auditData && (
          <>
            <Divider />

            {/* Request Information */}
            <Card className="info-card" style={{ marginBottom: 24 }}>
              <Title level={4}>Información de la Solicitud</Title>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div className="info-row">
                  <Text strong>Número de Solicitud:</Text>
                  <Text>{auditData.requestNumber}</Text>
                </div>
                <div className="info-row">
                  <Text strong>Tipo:</Text>
                  <Tag color={getTypeColor(auditData.type)}>
                    {auditData.type === 'LOAD' ? 'Carga' : 'Retiro'}
                  </Tag>
                </div>
                <div className="info-row">
                  <Text strong>Estado:</Text>
                  <Tag color={getStatusColor(auditData.status)}>{auditData.status}</Tag>
                </div>
                <div className="info-row">
                  <UserOutlined /> <Text strong>Usuario:</Text>
                  <Text>{auditData.username}</Text>
                </div>
                <div className="info-row">
                  <DollarOutlined /> <Text strong>Monto:</Text>
                  <Text>${auditData.amount.toLocaleString('es-AR')}</Text>
                </div>
                <div className="info-row">
                  <ClockCircleOutlined /> <Text strong>Creado:</Text>
                  <Text>{formatDate(auditData.createdAt)}</Text>
                </div>
                {auditData.processedAt && (
                  <div className="info-row">
                    <ClockCircleOutlined /> <Text strong>Procesado:</Text>
                    <Text>{formatDate(auditData.processedAt)}</Text>
                  </div>
                )}
              </Space>
            </Card>

            {/* Executive Summary */}
            <Card
              className="summary-card"
              style={{
                marginBottom: 24,
                background: currentTheme === 'dark'
                  ? 'linear-gradient(135deg, #434343 0%, #262626 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: currentTheme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none'
              }}
            >
              <Title level={4} style={{ color: currentTheme === 'dark' ? token.colorText : 'white' }}>
                Resumen Ejecutivo
              </Title>
              <Space size="large">
                <div>
                  <Text style={{ color: currentTheme === 'dark' ? token.colorTextSecondary : 'rgba(255,255,255,0.85)' }}>
                    Total de Logs
                  </Text>
                  <div>
                    <Text strong style={{ fontSize: 24, color: currentTheme === 'dark' ? token.colorText : 'white' }}>
                      {auditData.auditTrail.summary.totalLogs}
                    </Text>
                  </div>
                </div>
                <Divider type="vertical" style={{ height: 60, borderColor: currentTheme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.3)' }} />
                <div>
                  <Text style={{ color: currentTheme === 'dark' ? token.colorTextSecondary : 'rgba(255,255,255,0.85)' }}>
                    Errores
                  </Text>
                  <div>
                    <Text strong style={{ fontSize: 24, color: currentTheme === 'dark' ? '#ff7875' : '#ff4d4f' }}>
                      {auditData.auditTrail.summary.errors}
                    </Text>
                  </div>
                </div>
                <Divider type="vertical" style={{ height: 60, borderColor: currentTheme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.3)' }} />
                <div>
                  <Text style={{ color: currentTheme === 'dark' ? token.colorTextSecondary : 'rgba(255,255,255,0.85)' }}>
                    Advertencias
                  </Text>
                  <div>
                    <Text strong style={{ fontSize: 24, color: currentTheme === 'dark' ? '#ffc53d' : '#faad14' }}>
                      {auditData.auditTrail.summary.warnings}
                    </Text>
                  </div>
                </div>
                <Divider type="vertical" style={{ height: 60, borderColor: currentTheme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.3)' }} />
                <div>
                  <Text style={{ color: currentTheme === 'dark' ? token.colorTextSecondary : 'rgba(255,255,255,0.85)' }}>
                    Duración (ms)
                  </Text>
                  <div>
                    <Text strong style={{ fontSize: 24, color: currentTheme === 'dark' ? token.colorText : 'white' }}>
                      {auditData.auditTrail.summary.duration}
                    </Text>
                  </div>
                </div>
              </Space>
            </Card>

            {/* Logs Section */}
            <Card className="logs-card">
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <Title level={4}>Logs de Auditoría ({filteredLogs.length})</Title>
                  <Select
                    value={logLevelFilter}
                    onChange={setLogLevelFilter}
                    style={{ width: 150 }}
                  >
                    <Option value="all">Todos los niveles</Option>
                    <Option value="debug">Debug</Option>
                    <Option value="info">Info</Option>
                    <Option value="warn">Warning</Option>
                    <Option value="error">Error</Option>
                  </Select>
                </div>
                <Input
                  placeholder="Buscar en logs (fase, mensaje, datos)..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                  size="large"
                />
              </div>

              {filteredLogs.length === 0 ? (
                <Empty description="No hay logs con este filtro" />
              ) : (
                <div className="logs-list">
                  {filteredLogs.map((log: AuditLog, index: number) => (
                    <Card key={index} className="log-item" size="small" style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Space>
                          <Tag color={getLogLevelColor(log.level)}>{log.level.toUpperCase()}</Tag>
                          <Tag color="blue">{log.phase}</Tag>
                          <Text type="secondary">{new Date(log.timestamp).toLocaleTimeString('es-AR')}</Text>
                          <Text type="secondary">({log.executionTime}ms)</Text>
                        </Space>
                      </div>
                      <Text strong>{log.message}</Text>
                      {log.data && Object.keys(log.data).length > 0 && (
                        <div style={{
                          marginTop: 12,
                          backgroundColor: currentTheme === 'dark' ? '#1f1f1f' : '#f5f5f5',
                          padding: '12px',
                          borderRadius: '4px',
                          border: currentTheme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e8e8e8'
                        }}>
                          <pre style={{
                            margin: 0,
                            fontSize: '13px',
                            fontFamily: 'Monaco, Consolas, monospace',
                            color: currentTheme === 'dark' ? '#e8e8e8' : '#333',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}>
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}

        {!loading && !auditData && !error && (
          <Empty
            description="Ingrese un número de solicitud para ver su auditoría"
            style={{ marginTop: 48 }}
          />
        )}
      </Card>
    </div>
  );
};
